// Event-bus transport. The dashboard subscribes to the server event bus and
// Zod-validates every frame against the FROZEN {type,ts,id,data} envelope before
// pushing it into the store. Invalid frames are dropped with a console warning
// so a malformed update never crashes the UI (SPEC DoD).
//
// Transport preference: WS /ws/events first (lower latency, bidirectional);
// if the WebSocket cannot establish a connection, fall back to the SSE channel
// GET /api/v1/events (identical envelope). Both are provided by the server.

import { apiUrl, config, wsUrl } from '../config';
import { parseEventFrame } from '../api/events';
import { store } from '../store/store';
import { createWsClient, type WsClient } from './ws';
import { getWebSocketImpl } from './wsImpl';

export interface EventBusHandle {
  close: () => void;
  /** 'ws' | 'sse' | null — the transport currently in use (for diagnostics). */
  readonly mode: 'ws' | 'sse' | null;
}

let handle: EventBusHandle | null = null;

// BF28 — the bus is authenticated (admin-only) on both transports now, and a WebSocket
// handshake cannot carry an Authorization header. In a BROWSER we never put a credential in
// the URL: the same-origin session cookie rides the handshake by itself, and a session JWT in
// a query string would land in every proxy access log, on every reconnect.
//
// The one case that has no cookie is the cross-origin embedded GUI (Tauri), which injects its
// token as `window.__MCPGRAPH__.BEARER` before the app boots. ONLY that injected value is
// passed as `?token=` — deliberately not the in-memory bearer from a normal password login,
// which is a full-lifetime session JWT. Exported for the test.
export function eventsWsUrl(): string {
  const embedded = config.BEARER;
  return wsUrl('/ws/events') + (embedded ? `?token=${encodeURIComponent(embedded)}` : '');
}

function ingest(raw: unknown): void {
  const r = parseEventFrame(raw);
  if (!r.ok) {
    if (typeof console !== 'undefined') {
      console.warn('[eventbus] dropping invalid frame', r.error.issues);
    }
    return;
  }
  store.applyFrame(r.value);
}

export function connectEventBus(): EventBusHandle {
  if (handle) return handle;

  let mode: 'ws' | 'sse' | null = null;
  let wsClient: WsClient | null = null;
  let sse: EventSource | null = null;
  let everOpened = false;
  let fellBack = false;
  let fallbackTimer: ReturnType<typeof setTimeout> | null = null;
  let closed = false;

  function startSse(): void {
    if (closed || fellBack) return;
    fellBack = true;
    mode = 'sse';
    // Tear down the WS attempt so it stops reconnecting.
    wsClient?.close();
    wsClient = null;
    if (typeof EventSource === 'undefined') {
      // No SSE either (e.g. non-browser): nothing more we can do.
      store.setConnected(false);
      return;
    }
    try {
      sse = new EventSource(apiUrl('/api/v1/events'), { withCredentials: true });
    } catch {
      store.setConnected(false);
      return;
    }
    sse.onopen = () => store.setConnected(true);
    sse.onmessage = (ev: MessageEvent) => ingest(ev.data);
    sse.onerror = () => {
      // EventSource reconnects on its own; reflect the gap.
      store.setConnected(false);
    };
  }

  // Give the WS a short window to connect before falling back to SSE.
  fallbackTimer = setTimeout(() => {
    if (!everOpened && !closed) startSse();
  }, 4000);

  mode = 'ws';
  wsClient = createWsClient({
    url: () => eventsWsUrl(),
    parse: 'json',
    WebSocketImpl: getWebSocketImpl(),
    onOpen: () => {
      everOpened = true;
      if (fallbackTimer) {
        clearTimeout(fallbackTimer);
        fallbackTimer = null;
      }
      store.setConnected(true);
    },
    onClose: () => {
      store.setConnected(false);
      // If we never managed to open the WS at all, the endpoint is likely
      // unavailable — switch to SSE rather than reconnect-looping the WS.
      if (!everOpened) startSse();
    },
    onMessage: (data) => ingest(data),
    onError: () => {
      /* surfaced via onClose -> fallback/reconnect */
    },
  });
  wsClient.connect();

  handle = {
    close() {
      closed = true;
      if (fallbackTimer) clearTimeout(fallbackTimer);
      wsClient?.close();
      wsClient = null;
      sse?.close();
      sse = null;
      store.setConnected(false);
    },
    get mode() {
      return mode;
    },
  };
  return handle;
}

export function disconnectEventBus(): void {
  handle?.close();
  handle = null;
  store.setConnected(false);
}
